import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { selfAssessmentAPI } from '../lib/api';
import { Brain, Mail, ShieldCheck, ArrowLeft, Lock, Download, Send, FileText, PlayCircle } from 'lucide-react';

const BG = '#F5FAFC';
const TERRA = '#0076A3';
const INK = '#002A38';
const LINE = '#E7E5E4';

const shell = { minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', fontFamily: 'DM Sans, sans-serif' };
const card = { background: '#fff', border: `1px solid ${LINE}`, borderRadius: 20, padding: '2rem', maxWidth: 480, width: '100%', boxShadow: '0 8px 30px rgba(28,25,23,0.05)' };
const h1 = { fontFamily: 'Cabinet Grotesk', fontSize: '1.6rem', fontWeight: 700, color: INK, letterSpacing: '-0.02em', lineHeight: 1.15 };
const bodyText = { fontSize: '0.92rem', color: '#57534E', lineHeight: 1.65 };
const label = { fontSize: '0.78rem', fontWeight: 700, color: '#44403C', display: 'block', marginBottom: 6 };
const input = { width: '100%', border: `1px solid ${LINE}`, borderRadius: 10, padding: '0.7rem 0.85rem', fontSize: '0.95rem', color: INK, outline: 'none', boxSizing: 'border-box' };
const btn = { background: TERRA, color: '#fff', border: 'none', borderRadius: 999, padding: '0.85rem 1.5rem', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', width: '100%', transition: 'background-color 0.2s, opacity 0.2s' };
const linkBtn = { background: 'none', border: 'none', color: TERRA, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 };
const smallBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, background: '#EDF4F7', border: `1px solid ${LINE}`, borderRadius: 8, padding: '0.4rem 0.65rem', fontSize: '0.76rem', fontWeight: 600, color: '#44403C', cursor: 'pointer' };

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const saveBlob = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
};

export default function SelfAssessmentPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('buy');           // buy | retrieve
  const [step, setStep] = useState('email');         // email | code | details | reports
  const [cfg, setCfg] = useState({ base: 5000, gst: 900, gstRate: 18, total: 5900 });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [intakeId, setIntakeId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [reports, setReports] = useState([]);
  const [inProgress, setInProgress] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    selfAssessmentAPI.config()
      .then(({ data }) => setCfg({ base: data.base_inr, gst: data.gst_inr, gstRate: data.gst_rate, total: data.total_inr }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const sendCode = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes('@') || !e.includes('.')) { toast.error('Enter a valid email address'); return; }
    setBusy(true);
    try {
      const { data } = await selfAssessmentAPI.requestCode(e);
      setCooldown(data.resend_in_seconds || 60);
      setStep('code');
      toast.success('We emailed you a 6-digit code');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the code');
    } finally { setBusy(false); }
  };

  const loadReports = async (id) => {
    try {
      const { data } = await selfAssessmentAPI.myReports(id);
      setReports(data.reports || []);
      setInProgress(data.in_progress || []);
    } catch { setReports([]); setInProgress([]); }
  };

  const verifyCode = async () => {
    if (code.trim().length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      const { data } = await selfAssessmentAPI.verifyCode(email.trim().toLowerCase(), code.trim());
      setIntakeId(data.intake_id);
      toast.success('Email verified');
      if (mode === 'retrieve') { await loadReports(data.intake_id); setStep('reports'); }
      else { setStep('details'); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'That code is invalid or expired');
    } finally { setBusy(false); }
  };

  const payAndStart = async () => {
    if (!firstName.trim()) { toast.error('First name is required'); return; }
    if (!lastName.trim()) { toast.error('Last name is required'); return; }
    const li = linkedin.trim();
    if (!li || !li.toLowerCase().includes('linkedin.com/')) { toast.error('Enter a valid LinkedIn profile URL'); return; }
    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error('Could not load the payment gateway. Please retry.'); setBusy(false); return; }
      const { data } = await selfAssessmentAPI.createOrder({
        intake_id: intakeId, first_name: firstName.trim(), last_name: lastName.trim(), linkedin_url: li,
      });
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: 'CHIRE',
        description: 'Psychometric Assessment',
        order_id: data.order_id,
        prefill: { name: data.prefill?.name || '', email: data.prefill?.email || '' },
        theme: { color: TERRA },
        handler: async (resp) => {
          try {
            const { data: v } = await selfAssessmentAPI.verifyPayment({
              intake_id: intakeId,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success('Payment successful — starting your assessment');
            navigate(`/behavioral/${v.token}`);
          } catch (e) {
            toast.error('Payment verification failed. Please contact support.');
            setBusy(false);
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (r) => {
        toast.error('Payment failed: ' + (r.error?.description || 'Please try again.'));
        setBusy(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start the payment');
      setBusy(false);
    }
  };

  const downloadPdf = async (sessionId, kind) => {
    const names = { profile: 'CHIRE-Your-Profile.pdf', full: 'CHIRE-Full-DISC-Report.pdf', receipt: 'CHIRE-Invoice.pdf' };
    try {
      const { data } = await selfAssessmentAPI.downloadReport(sessionId, intakeId, kind);
      saveBlob(data, names[kind] || 'report.pdf');
    } catch { toast.error('Could not download. Please try again.'); }
  };

  const resend = async (sessionId) => {
    try { await selfAssessmentAPI.resendReport(intakeId, sessionId); toast.success('Sent! Check your inbox in a moment.'); }
    catch { toast.error('Could not email your report.'); }
  };

  const switchMode = (m) => { setMode(m); setStep('email'); setCode(''); setFirstName(''); setLastName(''); setLinkedin(''); toast.dismiss(); };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; } };

  return (
    <div style={shell}>
      <div style={card} data-testid="self-assessment-page">
        {(step === 'email' || step === 'code') && (
          <button data-testid="self-back-home" onClick={() => navigate('/')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#78716C', fontSize: '0.82rem', cursor: 'pointer', marginBottom: '0.9rem', padding: 0 }}>
            <ArrowLeft size={15} /> Back to home
          </button>
        )}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#FFF3EC', color: TERRA, borderRadius: 999, padding: '5px 14px', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '1.1rem' }}>
          <Brain size={13} /> CHIRE · Psychometric Assessment
        </div>

        {/* Step: email */}
        {step === 'email' && (
          <div data-testid="self-step-email">
            <h1 style={h1}>{mode === 'retrieve' ? 'Retrieve your report' : 'Discover your working style'}</h1>
            <p style={{ ...bodyText, marginTop: '0.75rem' }}>
              {mode === 'retrieve'
                ? 'Already took the assessment? Verify your email to re-download or re-send your report.'
                : <>Take a science-backed DISC-style psychometric assessment and get a personal report on how you lead, communicate and collaborate. Takes about <strong>8–10 minutes</strong>.</>}
            </p>

            {mode === 'buy' && (
              <div style={{ margin: '1.25rem 0', padding: '0.9rem 1rem', background: '#EDF4F7', borderRadius: 12 }} data-testid="self-price-breakdown">
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#57534E', marginBottom: 5 }}>
                  <span>Assessment</span><span>{inr(cfg.base)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#57534E' }}>
                  <span>GST ({cfg.gstRate}%)</span><span>{inr(cfg.gst)}</span>
                </div>
                <div style={{ height: 1, background: LINE, margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: '#44403C', fontWeight: 700 }}>Total</span>
                  <span style={{ fontFamily: 'Cabinet Grotesk', fontWeight: 800, color: INK, fontSize: '1.25rem' }} data-testid="self-price">{inr(cfg.total)}</span>
                </div>
              </div>
            )}

            <label style={label} htmlFor="self-email">Your email</label>
            <div style={{ position: 'relative', marginBottom: '1.1rem', marginTop: mode === 'retrieve' ? '1.25rem' : 0 }}>
              <Mail size={16} color="#A8A29E" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input id="self-email" data-testid="self-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com" style={{ ...input, paddingLeft: 36 }} onKeyDown={(e) => e.key === 'Enter' && sendCode()} disabled={busy} />
            </div>
            <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={busy} data-testid="self-send-code" onClick={sendCode}>
              {busy ? 'Sending…' : 'Continue'}
            </button>

            <div style={{ marginTop: '1.1rem', textAlign: 'center' }}>
              {mode === 'buy'
                ? <button style={linkBtn} data-testid="self-switch-retrieve" onClick={() => switchMode('retrieve')}>Already paid? Retrieve your report</button>
                : <button style={linkBtn} data-testid="self-switch-buy" onClick={() => switchMode('buy')}>Buy a new assessment</button>}
            </div>
            {mode === 'buy' && (
              <p style={{ ...bodyText, fontSize: '0.75rem', marginTop: '0.9rem', display: 'flex', alignItems: 'center', gap: 6, color: '#78716C' }}>
                <ShieldCheck size={14} color="#7FB08A" /> We'll email a 6-digit code to confirm it's really you.
              </p>
            )}
          </div>
        )}

        {/* Step: code */}
        {step === 'code' && (
          <div data-testid="self-step-code">
            <button onClick={() => setStep('email')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#78716C', fontSize: '0.82rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
              <ArrowLeft size={15} /> Back
            </button>
            <h1 style={h1}>Enter your code</h1>
            <p style={{ ...bodyText, marginTop: '0.6rem' }}>We sent a 6-digit code to <strong>{email}</strong>. It expires in 5 minutes.</p>
            <input data-testid="self-code-input" inputMode="numeric" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••" style={{ ...input, textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.4rem', fontWeight: 700, margin: '1.25rem 0' }}
              onKeyDown={(e) => e.key === 'Enter' && verifyCode()} disabled={busy} />
            <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={busy} data-testid="self-verify-code" onClick={verifyCode}>
              {busy ? 'Verifying…' : 'Verify email'}
            </button>
            <button onClick={sendCode} disabled={cooldown > 0 || busy} data-testid="self-resend-code"
              style={{ background: 'none', border: 'none', color: cooldown > 0 ? '#A8A29E' : TERRA, cursor: cooldown > 0 ? 'default' : 'pointer', fontSize: '0.82rem', marginTop: '0.9rem', width: '100%' }}>
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
          </div>
        )}

        {/* Step: details + pay */}
        {step === 'details' && (
          <div data-testid="self-step-details">
            <h1 style={h1}>A few details</h1>
            <p style={{ ...bodyText, marginTop: '0.6rem' }}>All fields are required. Your report will be emailed to <strong>{email}</strong>.</p>
            <div style={{ display: 'flex', gap: 12, margin: '1.25rem 0 0.9rem' }}>
              <div style={{ flex: 1 }}>
                <label style={label} htmlFor="self-first">First name</label>
                <input id="self-first" data-testid="self-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jordan" style={input} disabled={busy} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={label} htmlFor="self-last">Last name</label>
                <input id="self-last" data-testid="self-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Rivera" style={input} disabled={busy} />
              </div>
            </div>
            <label style={label} htmlFor="self-linkedin">LinkedIn profile URL</label>
            <input id="self-linkedin" data-testid="self-linkedin" value={linkedin} onChange={(e) => setLinkedin(e.target.value)}
              placeholder="linkedin.com/in/your-profile" style={{ ...input, marginBottom: '1rem' }} disabled={busy} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#78716C', marginBottom: '1rem' }}>
              <span>Assessment + {cfg.gstRate}% GST</span>
              <span style={{ fontWeight: 700, color: INK }}>{inr(cfg.total)}</span>
            </div>
            <button style={{ ...btn, opacity: busy ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} disabled={busy} data-testid="self-pay-btn" onClick={payAndStart}>
              <Lock size={16} /> {busy ? 'Starting…' : `Pay ${inr(cfg.total)} & start`}
            </button>
            <p style={{ ...bodyText, fontSize: '0.72rem', marginTop: '0.9rem', textAlign: 'center', color: '#A8A29E' }}>
              Secure checkout via Razorpay · UPI, cards, net banking &amp; wallets
            </p>
          </div>
        )}

        {/* Step: reports (retrieve) */}
        {step === 'reports' && (
          <div data-testid="self-step-reports">
            <h1 style={h1}>Your assessments</h1>
            <p style={{ ...bodyText, marginTop: '0.5rem', marginBottom: '1.25rem' }}>For <strong>{email}</strong></p>

            {reports.length === 0 && inProgress.length === 0 && (
              <div style={{ padding: '1rem', background: '#EDF4F7', borderRadius: 12, ...bodyText, fontSize: '0.85rem' }} data-testid="self-no-reports">
                No completed assessments found for this email yet.
              </div>
            )}

            {reports.map((r) => (
              <div key={r.session_id} data-testid="self-report-card" style={{ border: `1px solid ${LINE}`, borderRadius: 14, padding: '1rem', marginBottom: '0.9rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontFamily: 'Cabinet Grotesk', fontWeight: 700, color: INK, fontSize: '1rem' }}>{r.style_label || 'Psychometric profile'}</div>
                    <div style={{ fontSize: '0.75rem', color: '#78716C', marginTop: 2 }}>
                      {fmtDate(r.completed_at)}{r.invoice_number ? ` · Invoice ${r.invoice_number}` : ''}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <button style={smallBtn} data-testid="self-dl-profile" onClick={() => downloadPdf(r.session_id, 'profile')}><Download size={13} /> Profile</button>
                  <button style={smallBtn} data-testid="self-dl-full" onClick={() => downloadPdf(r.session_id, 'full')}><FileText size={13} /> Full report</button>
                  {r.has_receipt && <button style={smallBtn} data-testid="self-dl-receipt" onClick={() => downloadPdf(r.session_id, 'receipt')}><FileText size={13} /> Invoice</button>}
                  <button style={{ ...smallBtn, background: '#FFF3EC', color: TERRA, borderColor: '#F5D7D0' }} data-testid="self-resend" onClick={() => resend(r.session_id)}><Send size={13} /> Email it to me</button>
                </div>
              </div>
            ))}

            {inProgress.map((p) => (
              <div key={p.token} data-testid="self-inprogress-card" style={{ border: `1px dashed ${TERRA}`, borderRadius: 14, padding: '1rem', marginBottom: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.85rem', color: '#44403C', fontWeight: 600 }}>You have a paid assessment to finish</span>
                <button style={{ ...smallBtn, background: TERRA, color: '#fff', border: 'none' }} data-testid="self-resume" onClick={() => navigate(`/behavioral/${p.token}`)}><PlayCircle size={14} /> Resume</button>
              </div>
            ))}

            <button style={{ ...linkBtn, marginTop: '0.5rem' }} data-testid="self-take-another" onClick={() => switchMode('buy')}>Take a new assessment ({inr(cfg.total)})</button>
          </div>
        )}
      </div>
    </div>
  );
}
